import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Chip
} from '@mui/material';
import { TrendingUp, TrendingDown } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

const MoneyFlowSection = ({ moneyFlow, balancedExits, balancedEntries, formatNumber }) => {
  const { t, i18n } = useTranslation();
  const language = i18n.language;

  if (!moneyFlow || (balancedExits.length === 0 && balancedEntries.length === 0)) {
    return null;
  }

  return (
    <Paper 
      elevation={0}
      sx={{ 
        mb: 2, 
        p: 2.5,
        bgcolor: '#ffffff',
        border: '1px solid #f1f5f9',
        borderRadius: 2
      }}
    >
      <Box mb={2}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.5}>
          <Typography 
            variant="caption" 
            sx={{ 
              color: '#64748b', 
              fontSize: '0.7rem', 
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: 0.5
            }}
          >
            💸 {t('dashboard.sectorRotation.moneyFlow')} - 3 ngày qua
          </Typography>
          <Typography 
            variant="caption" 
            sx={{ 
              color: '#94a3b8', 
              fontSize: '0.65rem',
              fontStyle: 'italic'
            }}
          >
            Based on score changes
          </Typography>
        </Box>
        {balancedExits.length > 0 && balancedEntries.length > 0 && (
          <Typography 
            variant="caption" 
            sx={{ 
              color: '#64748b', 
              fontSize: '0.65rem',
              display: 'block'
            }}
          >
            {moneyFlow.total_exit > 0 && `${formatNumber(moneyFlow.total_exit, 1)} pts out`} 
            {moneyFlow.total_exit > 0 && moneyFlow.total_entry > 0 && ' → '}
            {moneyFlow.total_entry > 0 && `${formatNumber(moneyFlow.total_entry, 1)} pts in`}
          </Typography>
        )}
      </Box>
      
      <Box display="flex" gap={3}>
        {/* Money OUT (Exit sectors) */}
        <Box flex={1}>
          <Box display="flex" alignItems="center" gap={1} mb={1.5}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#ef4444' }} />
            <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.75rem', fontWeight: 600 }}>
              {t('dashboard.sectorRotation.moneyOut')}
            </Typography>
          </Box>
          <Box display="flex" flexDirection="column" gap={1}>
            {balancedExits.map((flow, idx) => (
              <Box 
                key={idx}
                sx={{ 
                  p: 1.5,
                  bgcolor: '#fef2f2',
                  border: '1px solid #fee2e2',
                  borderRadius: 1.5
                }}
              >
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.5}>
                  <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#991b1b' }}>
                    {flow.sector_name?.[language] || flow.sector_code}
                  </Typography>
                  <Typography variant="caption" sx={{ fontSize: '0.7rem', color: '#dc2626' }}>
                    {new Date(flow.date).toLocaleDateString('vi-VN', { month: 'short', day: 'numeric' })}
                  </Typography>
                </Box>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Typography variant="caption" sx={{ fontSize: '0.65rem', color: '#991b1b', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <TrendingDown sx={{ fontSize: 12 }} />
                    Rank: #{flow.prev_rank} → #{flow.rank}
                  </Typography>
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <Chip 
                      label={`${formatNumber(flow.prev_score, 1)} → ${formatNumber(flow.score, 1)}`}
                      size="small"
                      sx={{ 
                        height: 18, 
                        fontSize: '0.65rem',
                        bgcolor: '#fee2e2',
                        color: '#991b1b',
                        fontWeight: 600,
                        '& .MuiChip-label': { px: 0.75, py: 0 }
                      }}
                    />
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        fontSize: '0.7rem', 
                        fontWeight: 700, 
                        color: '#dc2626',
                        bgcolor: '#fef2f2',
                        px: 0.75,
                        py: 0.25,
                        borderRadius: 0.5
                      }}
                    >
                      {formatNumber(flow.score_change, 1)}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            ))}
            {balancedExits.length === 0 && (
              <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: '0.75rem', fontStyle: 'italic' }}>
                {t('dashboard.sectorRotation.noExits')}
              </Typography>
            )}
          </Box>
        </Box>

        {/* Flow Arrow */}
        <Box display="flex" alignItems="center" justifyContent="center" sx={{ minWidth: 60 }}>
          <Typography sx={{ fontSize: '2rem', color: '#cbd5e1' }}>→</Typography>
        </Box>

        {/* Money IN (Entry sectors) */}
        <Box flex={1}>
          <Box display="flex" alignItems="center" gap={1} mb={1.5}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#10b981' }} />
            <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.75rem', fontWeight: 600 }}>
              {t('dashboard.sectorRotation.moneyIn')}
            </Typography>
          </Box>
          <Box display="flex" flexDirection="column" gap={1}>
            {balancedEntries.map((flow, idx) => (
              <Box 
                key={idx}
                sx={{ 
                  p: 1.5,
                  bgcolor: '#f0fdf4',
                  border: '1px solid #dcfce7',
                  borderRadius: 1.5
                }}
              >
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.5}>
                  <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#065f46' }}>
                    {flow.sector_name?.[language] || flow.sector_code}
                  </Typography>
                  <Typography variant="caption" sx={{ fontSize: '0.7rem', color: '#10b981' }}>
                    {new Date(flow.date).toLocaleDateString('vi-VN', { month: 'short', day: 'numeric' })}
                  </Typography>
                </Box>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Typography variant="caption" sx={{ fontSize: '0.65rem', color: '#065f46', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <TrendingUp sx={{ fontSize: 12 }} />
                    Rank: #{flow.prev_rank} → #{flow.rank}
                  </Typography>
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <Chip 
                      label={`${formatNumber(flow.prev_score, 1)} → ${formatNumber(flow.score, 1)}`}
                      size="small"
                      sx={{ 
                        height: 18, 
                        fontSize: '0.65rem',
                        bgcolor: '#dcfce7',
                        color: '#065f46',
                        fontWeight: 600,
                        '& .MuiChip-label': { px: 0.75, py: 0 }
                      }}
                    />
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        fontSize: '0.7rem', 
                        fontWeight: 700, 
                        color: '#10b981',
                        bgcolor: '#f0fdf4',
                        px: 0.75,
                        py: 0.25,
                        borderRadius: 0.5
                      }}
                    >
                      +{formatNumber(flow.score_change, 1)}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            ))}
            {balancedEntries.length === 0 && (
              <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: '0.75rem', fontStyle: 'italic' }}>
                {t('dashboard.sectorRotation.noEntries')}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
    </Paper>
  );
};

export default MoneyFlowSection;
